import { TokenService } from './tokenService'
import { USER_TYPE } from '../enums/userTypes'
import moment from 'moment'
import HTTP from './http'

class AuthService {
  constructor(options) {
    const { accessToken, refreshToken, responseErrorHandler } = options || {}
    if (!accessToken) throw new Error('Missing access token.')
    if (!refreshToken) throw new Error('Missing refresh token.')
    this.accessToken = accessToken
    this.refreshToken = refreshToken
    this.tokenDetails = initializeTokenService(accessToken, refreshToken)
    HTTP.interceptors.response.use(
      (response) => response,
      (error) => {
        responseErrorHandler?.(error)
        return Promise.reject(error)
      }
    )
  }

  setupRefreshTokenExpiryAlert(alertInSecondsBeforeExpiry, checkEveryNSeconds, callbackFunction) {
    const tokenService = new TokenService(this.accessToken, this.refreshToken)
    tokenService.setupRefreshTokenExpiryAlert(alertInSecondsBeforeExpiry, checkEveryNSeconds)
    if (callbackFunction) callbackFunction()
  }

  getUserID() {
    return this.tokenDetails?.userId || null
  }

  getUserEmail() {
    return this.tokenDetails?.userEmail || null
  }

  getUsername() {
    return this.tokenDetails?.userName || null
  }

  getRoles() {
    return this.tokenDetails?.roles || []
  }

  getAccessToken() {
    return this.tokenDetails?.accessToken || null
  }

  getRefreshToken() {
    return this.tokenDetails?.refreshToken || null
  }

  getAccessTokenExpiration() {
    return this.tokenDetails?.accessTokenExpiry || null
  }

  tokenHasExpired() {
    const expiry = this.getAccessTokenExpiration()
    return Boolean(expiry && moment().unix() > expiry)
  }

  isLoggedIn() {
    return Boolean(this.getAccessToken() && !this.tokenHasExpired())
  }

  logout() {
    TokenService.clearTokenDetails()
  }

  async get2FAQRImage(actor = USER_TYPE.USER) {
    const apiUrl = getApiUrl()
    if (!this.isLoggedIn()) {
      throw new Error('Invalid token.')
    }
    isValidActor(actor)
    const url = `${apiUrl}/${actor}/${this.getUserID()}/mfa-qr-code`
    const headers = getHttpHeaders(this.getAccessToken())
    const { data: response } = await HTTP.post(url, null, headers)

    return response
  }

  async enable2FA(actor = USER_TYPE.USER, mfaSecret, mfaVerificationCode) {
    if (mfaSecret === null || mfaSecret === undefined) {
      throw new Error('Missing secret.')
    }
    if (!mfaVerificationCode) {
      throw new Error('Missing verification code.')
    }
    const apiUrl = getApiUrl()
    if (!this.isLoggedIn()) {
      throw new Error('Invalid token.')
    }
    isValidActor(actor)
    const url = `${apiUrl}/${actor}/${this.getUserID()}`
    const headers = getHttpHeaders(this.getAccessToken())
    const payload = { mfaEnabled: true, mfaSecret, mfaVerificationCode }
    return await HTTP.patch(url, payload, headers).then(() => true)
  }

  async disable2FA(actor = USER_TYPE.USER) {
    const apiUrl = getApiUrl()
    if (!this.isLoggedIn()) {
      throw new Error('Invalid token.')
    }
    isValidActor(actor)
    const url = `${apiUrl}/${actor}/${this.getUserID()}`
    const headers = getHttpHeaders(this.getAccessToken())
    const payload = { mfaEnabled: false }
    return await HTTP.patch(url, payload, headers).then(() => true)
  }

  async is2FAEnabled(actor = USER_TYPE.USER) {
    const apiUrl = getApiUrl()
    if (!this.isLoggedIn()) {
      throw new Error('Invalid token.')
    }
    isValidActor(actor)
    const url = `${apiUrl}/${actor}/${this.getUserID()}`
    const headers = getHttpHeaders(this.getAccessToken())
    const actorDetails = await getActorDetails(url, headers)

    return Boolean(actorDetails.mfaEnabled)
  }
}

function getApiUrl() {
  const apiUrl = localStorage.getItem('API_ROOT')
  if (!apiUrl) {
    throw new Error('Missing "API_ROOT" in local storage.')
  }
  return apiUrl
}

function isValidActor(actor) {
  const allowedUserTypes = Object.values(USER_TYPE)
  if (!allowedUserTypes.includes(actor)) {
    throw new Error(`Invalid actor(${actor}) supplied, allowed values are ${allowedUserTypes.join(', ')}`)
  }
  return true
}

async function getActorDetails(url, headers) {
  return await HTTP.get(url, headers).then((response) => response.data)
}

function getHttpHeaders(accessToken) {
  return {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  }
}

function initializeTokenService(accessToken, refreshToken) {
  let tokenDetails = {}
  try {
    new TokenService(accessToken, refreshToken)
    tokenDetails = TokenService.getTokenDetails()
    return tokenDetails
  } catch {
    return tokenDetails
  }
}

export { AuthService }
